import React from 'react';
import { Link } from 'react-router-dom';
import { Bell, ArrowRight, Code, Palette, TrendingUp, Star, Check, MessageCircle, Play } from 'lucide-react';
import SEO from '../components/SEO';
import GlobalSearch from '../components/GlobalSearch';
import AdSlot from '../components/AdSlot';
import { Button } from '../components/ui/button';
import Site from '../data/siteConfig.json';

const Home: React.FC = () => {
  const services = [
    {
      icon: Code,
      title: 'Web Development',
      description: 'Custom websites built with modern technologies and best practices.',
    },
    {
      icon: Palette,
      title: 'Theme Design',
      description: 'Beautiful, responsive themes for Blogger and WordPress platforms.',
    },
    {
      icon: TrendingUp,
      title: 'Digital Marketing',
      description: 'SEO, content marketing, and strategies to grow your online presence.',
    },
  ];

  const websiteTypes = [
    { title: 'E-commerce', image: 'https://img.freepik.com/free-vector/shopping-online-landing-page-concept_23-2148539965.jpg?ga=GA1.1.2059016454.1766767179&semt=ais_hybrid&w=740&q=80' },
    { title: 'Education', image: 'https://img.freepik.com/premium-vector/education-website-landing-page-ui-template-design_585784-5.jpg?ga=GA1.1.2059016454.1766767179&semt=ais_hybrid&w=740&q=80' },
    { title: 'Business', image: 'https://img.freepik.com/premium-vector/growth-your-business-landing-page-template-teamwork-partnership-business-communicate-concept_580420-2448.jpg?ga=GA1.1.2059016454.1766767179&semt=ais_hybrid&w=740&q=80' },
    { title: 'Portfolio', image: 'https://img.freepik.com/free-vector/gradient-photographer-landing-page_23-2150269012.jpg?ga=GA1.1.2059016454.1766767179&semt=ais_hybrid&w=740&q=80' },
  ];

  const clientLogos = [
  {
    name: "Gocourse",
    src: "https://media.licdn.com/dms/image/v2/D560BAQGzGiP4hur-lQ/company-logo_200_200/company-logo_200_200/0/1693049260332?e=2147483647&v=beta&t=r66AnvGK_MCuljV_t6yR6HdXl3kXXfQMilhjuSXSutg",
  },
  {
    name: "MagicEdTech",
    src: "https://media.glassdoor.com/sqll/311572/magic-software-squareLogo-1624478614036.png",
  },
  {
    name: "AssignmentDon",
    src: "https://blogger.googleusercontent.com/img/a/AVvXsEj_M6mS5qFwMxEyH2kdyslefm1rcMugs1M3o9rSIfekknKLAhYvWp2wXkwefpTFXkvDJPSKR9mdWJoQ-4vXw1JtnCEG39wN9FJtMe0XPC0d523D5_ZZhpsBBcYc7CFtsqlyU4YYIL0WN4PxtjwtLjFm90Fe810xrJz6P-kunIjnJDV8htr2xxueSJ2v=s1390",
  },
  {
    name: "Discovery Shilajit",
    src: "https://www.discoveryshilajit.com/favicon.ico",
  },
  {
    name: "Amving",
    src: "https://www.amving.in/favicon.ico",
  },
  {
    name: "Database Provider",
    src: "https://databaseprovider.co.in/wp-content/uploads/2024/01/cropped-Black-White-Simple-Monochrome-Initial-Name-Logo-32x32.png",
  },
];

const reviews = [
  {
    name: "Anoop Shrivastav",
    description:
      "Excellent service and clean design. Theme performance is very fast and support is really helpful.",
  },
  {
    name: "Srikanth",
    description:
      "TheCodeBazaar themes are very professional. Easy to customize and perfect for client projects.",
  },
  {
    name: "Sumantha",
    description:
      "Worth every rupee. Lifetime access and great quality. I will definitely buy again.",
  },
];

  const testimonials = [
    {
      name: 'Pradeep Kumar',
      role: 'Blogger',
      content: 'The theme I purchased transformed my blog completely. Professional design and excellent support!',
      rating: 5,
    },
    {
      name: 'Aditya Saha',
      role: 'Business Owner',
      content: 'Thecodebazaar built our company website and exceeded all expectations. Highly recommended!',
      rating: 5,
    },
    {
      name: 'Suriya R',
      role: 'Course Creator',
      content: 'Their WordPress theme made it easy to launch my online course platform. Great value for money.',
      rating: 5,
    },
  ];

  const faqs = [
    {
      question: 'Do you provide ongoing support?',
      answer: 'Yes, all our themes include 6 months of free support. We also offer extended support packages and maintenance plans.',
    },
    {
      question: 'Can I use themes on multiple websites?',
      answer: 'Our regular license allows use on one website. Extended licenses are available for multiple sites.',
    },
    {
      question: 'Do you provide customization services?',
      answer: 'Yes! We offer custom development and theme customization services. Contact us for a quote.',
    },
    {
      question: 'How long does custom development take?',
      answer: 'Typical projects take 2-4 weeks depending on complexity and requirements.',
    },
  ];

  return (
    <>
      <SEO
        title="Home"
        description="Professional website templates, custom web development, and digital marketing services. Transform your online presence with TheCodeBazaar."
      />
      
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-sky-50 via-white to-blue-50 py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl sm:text-5xl md:text-6xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Build Your Dream Website
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-sky-500 to-blue-600">
                  With Premium Templates
                </span>
              </h1>
              <p className="text-lg sm:text-xl text-gray-600 mb-8">
                Professional website templates, custom development, and digital marketing services to elevate your online presence.
              </p>
              
              {/* Large Search Box */}
              <div className="max-w-2xl mx-auto mb-8">
                <GlobalSearch size="large" placeholder="Search themes, courses, tutorials..." />
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/templates">
                  <Button className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300">
                    Browse Templates
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/hire">
                  <Button variant="outline" className="border-2 border-sky-500 text-sky-500 hover:bg-sky-50 px-8 py-6 text-lg rounded-xl transition-all duration-300">
                    Hire Us
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Services</h2>
              <p className="text-lg text-gray-600">Everything you need to succeed online</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 group"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-sky-100 to-blue-100 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <service.icon className="w-8 h-8 text-sky-500" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600">{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Website Templates Section */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Premium Website Templates</h2>
              <p className="text-lg text-gray-600">Professional themes for Blogger & WordPress</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-12">
              <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Blogger Themes</h3>
                <p className="text-gray-600 mb-6">
                  Ready-to-use, customizable themes for your Blogger website. Easy installation and setup.
                </p>
                <Link to="/templates?category=blogger">
                  <Button className="bg-gradient-to-r from-sky-500 to-blue-600 text-white hover:shadow-lg transition-all">
                    View Blogger Themes
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>

              <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">WordPress Themes</h3>
                <p className="text-gray-600 mb-6">
                  Feature-rich WordPress themes with advanced functionality and modern design.
                </p>
                <Link to="/templates?category=wordpress">
                  <Button className="bg-gradient-to-r from-sky-500 to-blue-600 text-white hover:shadow-lg transition-all">
                    View WordPress Themes
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>

            <div className="text-center">
              <Link to="/templates">
                <Button variant="outline" className="border-2 border-sky-500 text-sky-500 hover:bg-sky-50 px-8 py-3 rounded-xl">
                  View All Templates
                </Button>
              </Link>
            </div>
          </div>
        </section>

<AdSlot slot="heroBanner" />

        {/* Website Types Showcase */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Built for Every Business Type</h2>
              <p className="text-lg text-gray-600">Specialized templates for your industry</p>
            </div>

            <div className="grid md:grid-cols-4 gap-6">
              {websiteTypes.map((type, index) => (
                <div
                  key={index}
                  className="group relative overflow-hidden rounded-2xl h-64 cursor-pointer"
                >
                  <img
                    src={type.image}
                    alt={type.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end">
                    <h3 className="text-white text-2xl font-semibold p-6">{type.title}</h3>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <div className="bg-gradient-to-br from-red-50 via-white to-pink-50 py-20">
        <div className="container mx-auto px-4 text-center">
          <div className="w-40 h-40 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-6 p-1">

            <a href={Site.youtube.channel}
            >
              <img src="/assets/wd.png" className="aspect-[5/5] bg-white w-full rounded-full" loading="lazy"/>
            </a>
          </div>
          <h1 className="text-3xl md:text-6xl font-bold text-gray-900 mb-6">
            {Site.youtube.name} YouTube
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            {Site.youtube.description}
          </p>
          <a
            href={Site.youtube.channel}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300">
              <Bell className="mr-2 w-5 h-5" />
              Subscribe Now
            </Button>
          </a>
        </div>
      </div>


        {/* Portfolio/Work Samples */}
        <section className="hidden py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Recent Work</h2>
              <p className="text-lg text-gray-600">Projects we're proud of</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {[1, 2, 3].map((item) => (
                <div
                  key={item}
                  className="bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-xl transition-all duration-300 group"
                >
                  <div className="h-48 bg-gradient-to-br from-sky-100 to-blue-100 overflow-hidden">
                    <img
                      src={`https://images.unsplash.com/photo-${1460925895917 + item}?w=600&q=80`}
                      alt={`Project ${item}`}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Project Title {item}</h3>
                    <p className="text-gray-600 text-sm mb-4">
                      A comprehensive solution built for a leading industry client.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <span className="px-3 py-1 bg-sky-50 text-sky-600 text-xs rounded-full">WordPress</span>
                      <span className="px-3 py-1 bg-blue-50 text-blue-600 text-xs rounded-full">Custom</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Client Trust Section */}
        <section className="py-20 bg-gradient-to-br from-sky-50 to-blue-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Trusted by Thousands</h2>
              <p className="text-lg text-gray-600">Join our growing community of satisfied clients</p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {/* WhatsApp Chat Screenshot Placeholder */}
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-center space-x-3 mb-4">
                  <MessageCircle className="w-6 h-6 text-green-500" />
                  <h3 className="font-semibold text-gray-900">Client Testimonials</h3>
                </div>
                <div className="space-y-4">
  {reviews.map((review, index) => (
    <div
      key={index}
      className="bg-green-50 p-4 md:p-5 rounded-xl border border-green-100 shadow-sm hover:shadow-md transition"
    >
      <p className="text-sm md:text-base text-gray-700 italic leading-relaxed">
        “{review.description}”
      </p>

      <p className="text-xs md:text-sm text-gray-500 mt-3 font-medium">
        — {review.name}
      </p>
    </div>
  ))}
</div>

              </div>

              {/* Client Logos */}
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h3 className="font-semibold text-gray-900 mb-6 text-center">Our Clients</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
  {clientLogos.map((logo, index) => (
    <div
      key={index}
      className="aspect-square bg-white border border-gray-200 rounded-xl flex items-center justify-center p-4 shadow-sm hover:shadow-md transition"
    >
      <img
        src={logo.src}
        alt={`${logo.name} logo`}
        className="max-h-20 object-contain transition duration-300"
        loading="lazy"
      />
    </div>
  ))}
</div>

              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">What Our Clients Say</h2>
              <p className="text-lg text-gray-600">Real feedback from real customers</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300"
                >
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 italic">"{testimonial.content}"</p>
                  <div>
                    <div className="font-semibold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-500">{testimonial.role}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Courses Preview */}
        <section className="hidden py-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Learn & Grow</h2>
              <p className="text-lg text-gray-600">Professional courses to master web development</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-8">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300"
                >
                  <div className="h-40 bg-gradient-to-br from-sky-100 to-blue-100"></div>
                  <div className="p-6">
                    <div className="flex items-center space-x-2 mb-3">
                      <span className="px-3 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                        Bestseller
                      </span>
                      <span className="text-yellow-400 text-sm flex items-center">
                        <Star className="w-4 h-4 fill-current mr-1" />
                        4.9
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Complete Course Title {i}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">
                      Master the skills needed to succeed in web development.
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-sky-500">$149</span>
                      <Button size="sm" className="bg-gradient-to-r from-sky-500 to-blue-600 text-white">
                        Enroll Now
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center">
              <Link to="/courses">
                <Button variant="outline" className="border-2 border-sky-500 text-sky-500 hover:bg-sky-50 px-8 py-3 rounded-xl">
                  View All Courses
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* YouTube Section */}
        <section className="hidden py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Learn on YouTube</h2>
              <p className="text-lg text-gray-600">Free tutorials and tips for beginners and experts</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-8">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="group cursor-pointer"
                >
                  <div className="relative overflow-hidden rounded-2xl mb-4">
                    <div className="aspect-video bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                      <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <Play className="w-8 h-8 text-white ml-1" />
                      </div>
                    </div>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-sky-500 transition-colors">
                    Tutorial Video Title {i}
                  </h3>
                  <p className="text-sm text-gray-600">120K views • 2 days ago</p>
                </div>
              ))}
            </div>

            <div className="text-center">
              <Link to="/youtube">
                <Button className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-xl">
                  Subscribe to Our Channel
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Hosting Affiliate Section */}
        <section className="py-20 bg-gradient-to-br from-sky-500 to-blue-600 text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-4xl font-bold mb-4">Get Premium Hosting at 60% OFF</h2>
              <p className="text-xl mb-8 text-sky-50">
                Power your website with lightning-fast, reliable hosting from Hostinger
              </p>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 mb-8">
                <div className="grid md:grid-cols-3 gap-6 mb-6">
                  <div>
                    <div className="text-3xl font-bold mb-2">99.9%</div>
                    <div className="text-sky-100">Uptime</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold mb-2">24/7</div>
                    <div className="text-sky-100">Support</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold mb-2">Free</div>
                    <div className="text-sky-100">SSL & Domain</div>
                  </div>
                </div>

                <ul className="space-y-3 text-left max-w-md mx-auto mb-6">
                  {['WordPress optimized', 'Daily backups', 'Free migration', 'Money-back guarantee'].map((feature) => (
                    <li key={feature} className="flex items-center space-x-3">
                      <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <a
                href="https://www.hostg.xyz/SHCo6"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block"
              >
                <Button className="bg-white text-sky-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300">
                  Get Hosting Now - 60% OFF
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* Hire Us CTA */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto bg-gradient-to-br from-sky-50 to-blue-50 rounded-3xl p-12 text-center border border-sky-200">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Need a Custom Website?
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Our expert team is ready to bring your vision to life with custom web development
              </p>
              <Link to="/hire">
                <Button className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300">
                  Hire Us Today
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

<AdSlot slot="heroBanner2" />

        {/* FAQ Section */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
              <p className="text-lg text-gray-600">Got questions? We've got answers</p>
            </div>

            <div className="max-w-3xl mx-auto space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300"
                >
                  <h3 className="font-semibold text-gray-900 mb-2">{faq.question}</h3>
                  <p className="text-gray-600">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Home;
